import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Divider, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import settings from '/@/core/settings';
import type { User, UserNode } from '/@/hooks/nodes';
import { useAllUserNodesLookup, useOwnUserNode } from '/@/hooks/nodes';
import type useGeneratePDF from '/@/hooks/pdf';
import { useShareRecommendationMutation } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import type { Recommendation } from '/@/types/recommendation';
import { isMobile, openWhatsappWeb } from '/@/utils';
import UserCard from '../../bayer/user-card';

interface ShareWithOthersProps {
    item?: Recommendation;
    onEmail?: ReturnType<typeof useGeneratePDF>['handleEmail'];
    setCurrentStep: (step: number) => void;
    setSharedWith: (sharedWith: string | undefined) => void;
    handleWhatsApp: (phone?: string, user?: User, userNodeId?: number) => void;
    ownUserNodeId?: number;
}

const ShareWithOthers = ({
    item,
    onEmail,
    setCurrentStep,
    setSharedWith,
    handleWhatsApp,
    ownUserNodeId,
}: ShareWithOthersProps) => {
    const allUserNodesLookup = useAllUserNodesLookup();

    // Combine ancestors and children, excluding self, and sort based on current user type
    const allUserNodes = useMemo(() => {
        const sortTypes = ['squad_coach', 'consultant', 'rv', 'advisor', 'grower'];
        const userNodeId = item?.userNode;

        // Create a map of nodes by id for quick lookup
        const nodesMap = allUserNodesLookup.allNodesById;

        // Get parent chain recursively: parent, parent of parent, parent of parent of parent...
        const parentChain: UserNode[] = [];
        if (userNodeId) {
            // Start from the userNodeId and traverse up all parent chains (a UserNode can have multiple parents)
            const queue: number[] = [userNodeId];
            const visitedIds = new Set<number>(); // Prevent infinite loops

            while (queue.length > 0) {
                const currentNodeId = queue.shift();
                if (currentNodeId === undefined || visitedIds.has(currentNodeId)) continue;
                visitedIds.add(currentNodeId);

                const currentNode = nodesMap.get(currentNodeId);
                if (currentNode) {
                    parentChain.push(currentNode);
                }

                const parentIds = currentNode?.parents ?? [];
                for (const parentId of parentIds) {
                    const parentNode = nodesMap.get(parentId);
                    if (parentNode && !visitedIds.has(parentNode.id)) {
                        queue.push(parentNode.id);
                    }
                }
            }
        }

        // Filter out self (current user's own node) and filter by allowed types
        const filteredNodes = parentChain.filter((node) => node.id !== ownUserNodeId && sortTypes.includes(node.type));

        // Sort by type priority
        return filteredNodes.sort((a, b) => {
            const aIndex = sortTypes.indexOf(a.type);
            const bIndex = sortTypes.indexOf(b.type);
            // If type not in sortTypes, put it at the end
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
    }, [allUserNodesLookup.allNodesById, ownUserNodeId, item?.userNode]);

    const isLoading = allUserNodesLookup.isValidating || allUserNodesLookup.isLoading;

    if (isLoading) {
        return (
            <Stack spacing={1} width='100%'>
                {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton variant='rectangular' height={80} key={`skeleton-loading-${index + 1}`} />
                ))}
            </Stack>
        );
    }

    if (allUserNodes.length === 0) {
        return (
            <Typography variant='body2' color='primary.mui.inactive'>
                {_('No hay contactos disponibles.')}
            </Typography>
        );
    }

    return (
        <>
            <Typography variant='body2' fontWeight='bold' color='primary' sx={{ textAlign: 'left' }}>
                {_('Personas relacionadas con la recomendación')}
            </Typography>
            <Stack className='mp-mask' width='100%' spacing={1.5} maxHeight={400} overflow='auto'>
                {allUserNodes.map((userNode) => (
                    <Box key={`user-node-${userNode.id}`}>
                        <UserCard
                            userNode={userNode}
                            onWhatsAppClick={() => handleWhatsApp(userNode?.phone, userNode?.user, userNode.id)}
                            onEmailClick={async (e) => {
                                e?.stopPropagation();
                                if (!onEmail) return;
                                await onEmail({ email: userNode?.email });
                                setSharedWith(userNode?.email || '');
                                setCurrentStep(2);
                            }}
                        />
                    </Box>
                ))}
            </Stack>
        </>
    );
};

interface ShareButtonsProps {
    onWhatsAppClick: () => void;
    onEmailClick: () => void;
}

const ShareButtons = ({ onWhatsAppClick, onEmailClick }: ShareButtonsProps) => (
    <Stack direction='row' spacing={1}>
        <Stack direction='column' spacing={1} alignItems='center'>
            <IconButton onClick={onWhatsAppClick} color='primary' data-testid='share-item-popup-whatsapp-button'>
                <WhatsAppIcon sx={{ fontSize: 24 }} />
            </IconButton>
            <Typography variant='caption' color='primary'>
                {_('Whatsapp')}
            </Typography>
        </Stack>
        <Stack direction='column' spacing={1} alignItems='center'>
            <IconButton onClick={onEmailClick} color='primary' data-testid='share-item-popup-email-button'>
                <EmailOutlinedIcon sx={{ fontSize: 24 }} />
            </IconButton>
            <Typography variant='caption' color='primary'>
                {_('Email')}
            </Typography>
        </Stack>
    </Stack>
);

interface InitialStepWithNodesProps {
    item?: Recommendation;
    onWhatsApp?: ReturnType<typeof useGeneratePDF>['handleWhatsApp'];
    onEmail?: ReturnType<typeof useGeneratePDF>['handleEmail'];
    setCurrentStep: (step: number) => void;
    setSharedWith: (sharedWith: string | undefined) => void;
}

function InitialStepWithNodes({ item, onWhatsApp, onEmail, setCurrentStep, setSharedWith }: InitialStepWithNodesProps) {
    const { trigger: shareRecommendation } = useShareRecommendationMutation();

    const ownUserNodeQuery = useOwnUserNode();

    const handleWhatsApp = async (phone?: string, user?: User, userNodeId?: number) => {
        let message: string;
        if (user) {
            const url = (settings as any).app?.url + item?.frontendUrl;
            message = _(
                'Hola! Te envío una recomendación que generé en {{appName}}. Podés verla y dejar tus comentarios {{url}}',
                { appName: (settings as any).app?.name, url }
            );

            // Create share record when sharing with a user
            if (item?.id && userNodeId) {
                try {
                    await shareRecommendation({
                        recommendationId: item.id,
                        sharedWith: userNodeId,
                    });
                } catch (error) {
                    console.error('Failed to create share record:', error);
                }
            }
        } else {
            if (!onWhatsApp) return;
            const pdfResult = await onWhatsApp();
            if (pdfResult) {
                const url = (settings as any).app?.url + pdfResult.file.url;
                message = _(
                    'Hola! Te envío una recomendación que generé en {{appName}}. Abrila para ver todos los detalles {{url}}',
                    { appName: (settings as any).app?.name, url }
                );
            } else {
                return;
            }
        }
        if (isMobile()) {
            const encodedMessage = encodeURIComponent(message);
            let url = `whatsapp://send?text=${encodedMessage}`;
            if (phone) {
                url += `&phone=${phone}`;
            }
            window.location.href = url;
        } else {
            openWhatsappWeb({ message, phone });
        }
        setSharedWith(phone ? phone : undefined);
        setCurrentStep(2);
    };

    const showShareWithOthers = useMemo(() => {
        const isMyNode = ownUserNodeQuery.data?.id === item?.userNode;
        const imGrower = ownUserNodeQuery.data?.type === 'grower';
        // Show related contacts if: it's not my node OR if it's my node but I'm a grower
        return !isMyNode || (isMyNode && imGrower);
    }, [ownUserNodeQuery.data, item?.userNode]);

    if (showShareWithOthers) {
        return (
            <Stack width='100%' spacing={2}>
                <ShareWithOthers
                    item={item}
                    onEmail={onEmail}
                    setCurrentStep={setCurrentStep}
                    setSharedWith={setSharedWith}
                    handleWhatsApp={handleWhatsApp}
                    ownUserNodeId={ownUserNodeQuery.data?.id}
                />
                <Divider />
                <Stack textAlign='left'>
                    <Typography variant='body2' fontWeight='bold' color='primary'>
                        {_('Otras personas')}
                    </Typography>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {_('Compartí la recomendación por mail o por Whatsapp')}
                    </Typography>
                </Stack>
                <ShareButtons
                    onWhatsAppClick={() => handleWhatsApp(undefined, undefined, undefined)}
                    onEmailClick={() => setCurrentStep(1)}
                />
            </Stack>
        );
    }

    return (
        <Stack spacing={3} alignItems='center'>
            <Typography color='primary'>{_('Envía la recomendación por mail o por Whatsapp')}</Typography>
            <ShareButtons
                onWhatsAppClick={() => handleWhatsApp(undefined, undefined, undefined)}
                onEmailClick={() => setCurrentStep(1)}
            />
        </Stack>
    );
}

export default InitialStepWithNodes;
